import React, { useState, useEffect } from 'react'
import { Box, TextField, Grid, Typography, CircularProgress } from '@mui/material'
import { StyledButton } from '../UI/StyledButton'
import Header from '../UI/Header'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import * as XLSX from 'xlsx'
import { format } from 'date-fns'
import ResultsTableSoggettoTerzo from '../Charts/ResultsTableSoggettoTerzo'
import DynamicFilterMenu from './components/DynamicFilterMenu'
import { searchSoggettiTerzi } from '../services/searchSoggettiTerzi'

const MAX_FILTERS = 14

const SoggettiTerzi = () => {
  const theme = useTheme()
  const navigate = useNavigate()

  const [searchString, setSearchString] = useState('')
  const [searchResult, setSearchResult] = useState([])
  const [isSearching, setIsSearching] = useState(false)

  const { data: listaSoggetti, isLoading, refetch } = useQuery(
    ['searchSoggettiTerzi'],
    () => searchSoggettiTerzi(null),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      staleTime: 5 * 60 * 1000,
      onSuccess: (data) => setSearchResult(data.data),
    },
  )

  const [anchorEl, setAnchorEl] = useState(null)
  const [open, setOpen] = useState(false)
  const [activeColumns, setActiveColumns] = useState([])
  const [count, setCount] = useState(0)

  const [filtered, setFiltered] = useState(null)
  const [isFiltering, setIsFiltering] = useState(false)

  const [cognome, setCognome] = useState('')
  const [textSearchingCognome, setTextSearchingCognome] = useState('Contiene')

  const [nome, setNome] = useState('')
  const [textSearchingNome, setTextSearchingNome] = useState('Contiene')

  const [codiceFiscale, setCodiceFiscale] = useState('')
  const [textSearchingCodiceFiscale, setTextSearchingCodiceFiscale] = useState('Contiene')

  const [ndgClienteCollegato, setNdgClienteCollegato] = useState('')
  const [textSearchingNdgClienteCollegato, setTextSearchingNdgClienteCollegato] = useState('Contiene')

  const [saleDiRiferimento, setSaleDiRiferimento] = useState('')
  const [textSearchingSaleDiRiferimento, setTextSearchingSaleDiRiferimento] = useState('Contiene')

  const [esito, setEsito] = useState('')
  const [textSearchingEsito, setTextSearchingEsito] = useState('Uguale')

  const [pivaClienteCollegato, setPivaClienteCollegato] = useState('')
  const [textSearchingPivaClienteCollegato, setTextSearchingPivaClienteCollegato] = useState('Contiene')
                
  const [dataScreening, setDataScreening] = useState(null)
  const [dataScreeningA, setDataScreeningA] = useState(null)
  const [dateSearchingScreening, setDateSearchingScreening] = useState('Uguale')

  useEffect(() => {
    refetch()
  }, [refetch])

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget)
    setOpen(true)
  }
  const handleCloseMenu = () => {
    setAnchorEl(null)
    setOpen(false)
  }

  const handleColumnChange = (rowIndex, value) => {
    setActiveColumns(prev => {
      const next = [...prev]
      next[rowIndex] = value
      return next
    })
  }

  const handleAdd = () => setCount(c => Math.min(c + 1, MAX_FILTERS))

  const handleClearAll = () => {
    setCognome(''); setNome(''); setCodiceFiscale('')
    setNdgClienteCollegato('')
    setSaleDiRiferimento(''); setEsito(''); setPivaClienteCollegato('')
    setDataScreening(null); setDataScreeningA(null)
  }

  const handleRemoveAll = () => {
    setCount(0)
    setActiveColumns([])
    handleClearAll()
  }

  const handleRemove = () => {
    setCount(c => {
      const next = Math.max(0, c - 1)
      if (next === 0) {
        setActiveColumns([])
        handleClearAll()
      } else {
        setActiveColumns(prev => prev.slice(0, next))
      }
      return next
    })
  }

  const resetToInitialList = () => {
    setFiltered([])
    setSearchString('')
    setSearchResult(listaSoggetti?.data || [])
    setIsSearching(false)
    setIsFiltering(false)
    handleRemoveAll()
  }

  const handleRowClick = (params) => {
    navigate('/dettaglioSoggettoTerzo', { state: { selectedRowData: params.row } })
  }

  const handleDownloadExcel = () => {
    const dataToExport = filtered || []

    const mappedData = dataToExport.map((item) => ({
      cognome: item.cognome,
      nome: item.nome,
      codiceFiscale: item.codiceFiscale,
      dataScreening: item.dataScreening ? dayjs(item.dataScreening).format('DD/MM/YYYY') : '',
      esito: item.esito,
      ndgClienteCollegato: item.ndgClienteCollegato,
      saleDiRiferimento: item.saleDiRiferimento,
      pivaClienteCollegato: item.pivaClienteCollegato,
    }))

    const ws = XLSX.utils.json_to_sheet(mappedData)
    ws['!cols'] = Array(8).fill({ wch: 20 })

    const headerNames = [
      'Cognome', 'Nome', 'Codice Fiscale',
      'Data  Screening','Esito', 'NDG Cliente Collegato', 'Sale di Riferimento',
      'PIVA Cliente Collegato'
    ]

    XLSX.utils.sheet_add_aoa(ws, [headerNames], { origin: 'A1' })

    const formattedDate = format(new Date(), 'ddMMyyyy')
    const fileName = `soggetti_terzi_${formattedDate}.xlsx`
    XLSX.writeFile({ Sheets: { 'Dati': ws }, SheetNames: ['Dati'] }, fileName)
  }

  const applyTextFilter = (data, value, searchType, fieldGetter) => {
    if (!value) return data
    if (searchType === 'Uguale') {
      return data.filter(item => fieldGetter(item) === value)
    }
    if (searchType === 'Contiene') {
      return data.filter(item => (fieldGetter(item) || '').toLowerCase().includes(value.toLowerCase()))
    }
    return data
  }

  const applyDateFilter = (data, valueDa, valueA, searchType, fieldGetter) => {
    if (!valueDa) return data
    const da = dayjs(valueDa).startOf('day')

    if (searchType === 'Maggiore') {
      return data.filter(item => dayjs(fieldGetter(item)).isAfter(da) || dayjs(fieldGetter(item)).isSame(da))
    }
    if (searchType === 'Minore') {
      return data.filter(item => dayjs(fieldGetter(item)).isBefore(da) || dayjs(fieldGetter(item)).isSame(da))
    }
    if (searchType === 'Uguale') {
      return data.filter(item => dayjs(fieldGetter(item)).isSame(da, 'day'))
    }
    if (searchType === 'Periodo' && valueA) {
      const a = dayjs(valueA).startOf('day')
      return data.filter(item => {
        const d = dayjs(fieldGetter(item))
        return (d.isAfter(da) || d.isSame(da, 'day')) && (d.isBefore(a) || d.isSame(a, 'day'))
      })
    }
    return data
  }

  function handleFiltra() {
    let filteredData = listaSoggetti?.data || []
    setIsFiltering(true)

    filteredData = applyTextFilter(filteredData, cognome, textSearchingCognome, i => i.cognome)
    filteredData = applyTextFilter(filteredData, nome, textSearchingNome, i => i.nome)
    filteredData = applyTextFilter(filteredData, codiceFiscale, textSearchingCodiceFiscale, i => i.codiceFiscale)
    filteredData = applyTextFilter(filteredData, ndgClienteCollegato, textSearchingNdgClienteCollegato, i => i.ndgClienteCollegato)
    filteredData = applyTextFilter(filteredData, saleDiRiferimento, textSearchingSaleDiRiferimento, i => i.saleDiRiferimento)
    filteredData = applyTextFilter(filteredData, esito, textSearchingEsito, i => i.esito)
    filteredData = applyTextFilter(filteredData, pivaClienteCollegato, textSearchingPivaClienteCollegato, i => i.pivaClienteCollegato)

    filteredData = applyDateFilter(filteredData, dataScreening, dataScreeningA, dateSearchingScreening, i => i.dataScreening)

    setFiltered(filteredData)
  }

  const handleSearch = async () => {
    if (!searchString.trim()) {
      setSearchResult(listaSoggetti?.data || [])
      setIsSearching(false)
      return
    }
    setIsSearching(true)
    try {
      const response = await searchSoggettiTerzi(searchString)
      setSearchResult(response.data || [])
    } catch (error) {
      console.error('Errore nella ricerca:', error)
      setSearchResult([])
    } finally {
      setIsSearching(false)
    }
  }

  const handleInputChange = (event) => setSearchString(event.target.value)

  const fields = [
    {
      label: 'Cognome', type: 'text',
      searchTypeOptions: ['Uguale', 'Contiene'],
      searchType: textSearchingCognome, onSearchTypeChange: (e) => setTextSearchingCognome(e.target.value),
      value: cognome, onChange: (e) => setCognome(e.target.value), onClear: () => setCognome(''),
    },
    {
      label: 'Nome', type: 'text',
      searchTypeOptions: ['Uguale', 'Contiene'],
      searchType: textSearchingNome, onSearchTypeChange: (e) => setTextSearchingNome(e.target.value),
      value: nome, onChange: (e) => setNome(e.target.value), onClear: () => setNome(''),
    },
    {
      label: 'Codice Fiscale', type: 'text',
      searchTypeOptions: ['Uguale', 'Contiene'],
      searchType: textSearchingCodiceFiscale, onSearchTypeChange: (e) => setTextSearchingCodiceFiscale(e.target.value),
      value: codiceFiscale, onChange: (e) => setCodiceFiscale(e.target.value), onClear: () => setCodiceFiscale(''),
    },
    {
      label: 'Data Screening', type: 'dateRange',
      dateSearchTypeOptions: ['Uguale', 'Maggiore', 'Minore', 'Periodo'],
      dateSearchType: dateSearchingScreening, onDateSearchTypeChange: (e) => setDateSearchingScreening(e.target.value),
      valueDa: dataScreening, onChangeDa: (d) => setDataScreening(d), onClearDa: () => setDataScreening(null),
      valueA: dataScreeningA, onChangeA: (d) => setDataScreeningA(d), onClearA: () => setDataScreeningA(null),
    },
    {
      label: 'Esito', type: 'select',
      options: ['PRESENTE', 'NON PRESENTE'],
      searchTypeOptions: ['Uguale'],
      searchType: textSearchingEsito, onSearchTypeChange: (e) => setTextSearchingEsito(e.target.value),
      value: esito, onChange: (e) => setEsito(e.target.value), onClear: () => setEsito(''),
    },
    {
      label: 'NDG Cliente Collegato', type: 'text',
      searchTypeOptions: ['Uguale', 'Contiene'],
      searchType: textSearchingNdgClienteCollegato, onSearchTypeChange: (e) => setTextSearchingNdgClienteCollegato(e.target.value),
      value: ndgClienteCollegato, onChange: (e) => setNdgClienteCollegato(e.target.value), onClear: () => setNdgClienteCollegato(''),
    },
    {
      label: 'Sale di Riferimento', type: 'text',
      searchTypeOptions: ['Uguale', 'Contiene'],
      searchType: textSearchingSaleDiRiferimento, onSearchTypeChange: (e) => setTextSearchingSaleDiRiferimento(e.target.value),
      value: saleDiRiferimento, onChange: (e) => setSaleDiRiferimento(e.target.value), onClear: () => setSaleDiRiferimento(''),
    },
    {
      label: 'PIVA Cliente Collegato', type: 'text',
      searchTypeOptions: ['Uguale', 'Contiene'],
      searchType: textSearchingPivaClienteCollegato, onSearchTypeChange: (e) => setTextSearchingPivaClienteCollegato(e.target.value),
      value: pivaClienteCollegato, onChange: (e) => setPivaClienteCollegato(e.target.value), onClear: () => setPivaClienteCollegato(''),
    },
  ]

  return (
    <div>
      <Header
        showHome={true}
        showRichiesta={false}
        showStatistiche={false}
        showAccountingMonitoraggio={false}
        showSoggettiTerzi={true}
        header={
          <Grid container spacing={2} alignItems="center" justifyContent="center">
            <Grid item xs={12} sx={{ textAlign: 'center', height: '40px', marginTop: '1rem' }}>
              <Typography sx={{ flexGrow: 1 }} mb={'1rem'} color={theme.palette.black.main} variant="h1">
                SOGGETTI TERZI
              </Typography>
            </Grid>
            <Grid item>
              <TextField
                margin="normal"
                placeholder="Cerca"
                value={searchString}
                variant="standard"
                id="search"
                name="search"
                autoComplete="search"
                onChange={handleInputChange}
                autoFocus
                style={{ flexGrow: 1, marginRight: '10px' }}
              />
              <StyledButton
                onClick={handleSearch}
                sx={{ backgroundColor: theme.palette.grey.light, color: theme.palette.black.main, border: '0px', paddingRight: '10px', mr: 4, mt: 2 }}
                disabled={!searchString.trim()}
              >
                <Typography variant="caption">CERCA</Typography>
              </StyledButton>
              <StyledButton
                sx={{ backgroundColor: theme.palette.grey.light, color: theme.palette.black.main, border: '0px', paddingRight: '10px', mr: 4, mt: 2 }}
                onClick={() => resetToInitialList()}
              >
                <Typography variant="caption">ANNULLA</Typography>
              </StyledButton>
            </Grid>
          </Grid>
        }
      />
      {isLoading && !isSearching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={0} columns={{ xs: 4, sm: 8, md: 12 }} justifyItems="space-between"
          sx={{ marginLeft: '2vw', marginRight: '2vw', maxWidth: '96vw' }}>
          <Grid item xs={12}>
            <StyledButton
              onClick={handleOpenMenu}
              sx={{ backgroundColor: theme.palette.grey.light, color: theme.palette.black.main, border: '0px', paddingRight: '10px', ml: 0, mt: 0 }}
            >
              <Typography variant="caption">FILTRI</Typography>
            </StyledButton>

            <DynamicFilterMenu
              anchorEl={anchorEl}
              open={open}
              handleClose={handleCloseMenu}
              handleAdd={handleAdd}
              handleRemove={handleRemove}
              handleFiltra={handleFiltra}
              handleDownloadExcel={handleDownloadExcel}
              resetToInitialList={resetToInitialList}
              isFiltering={isFiltering}
              fields={fields}
              activeColumns={activeColumns}
              onColumnChange={handleColumnChange}
              count={count}
              maxFilters={MAX_FILTERS}
            />

            <Box sx={{ padding: 0, width: '100%', height: { xs: '500px', xl: '650px' } }}>
              <ResultsTableSoggettoTerzo
                rows={isFiltering ? filtered : searchResult}
                isLoading={isLoading && !isSearching}
                onRowClick={handleRowClick}
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </div>
  )
}

export default SoggettiTerzi
